import React from 'react';
import { Github, Linkedin, Mail, MapPin, Phone, ArrowRight, Star, Sparkles, Code, Heart, Zap } from 'lucide-react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { useInView } from 'react-intersection-observer';

const Footer: React.FC = () => {
  const [ref, inView] = useInView({
    triggerOnce: true,
    threshold: 0.1,
  });

  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: 0.1,
        delayChildren: 0.2,
      },
    },
  };

  const itemVariants = {
    hidden: { opacity: 0, y: 20 },
    visible: {
      opacity: 1,
      y: 0,
      transition: {
        duration: 0.6,
        ease: [0.17, 0.55, 0.55, 1],
      },
    },
  };

  const socialVariants = {
    hidden: { opacity: 0, scale: 0 },
    visible: {
      opacity: 1,
      scale: 1,
      transition: {
        duration: 0.5,
        type: "spring",
        stiffness: 100,
      },
    },
  };

  const socialLinks = [
    {
      href: "https://github.com/Vonzippo",
      icon: Github,
      label: "GitHub",
      color: "hover:text-purple-400",
      bgColor: "hover:bg-purple-500/20"
    },
    {
      href: "https://www.linkedin.com/in/patrick-oetterli/",
      icon: Linkedin,
      label: "LinkedIn",
      color: "hover:text-blue-400",
      bgColor: "hover:bg-blue-500/20"
    },
    {
      href: "mailto:patrick.oetterli@gmail.com",
      icon: Mail,
      label: "E-Mail",
      color: "hover:text-green-400",
      bgColor: "hover:bg-green-500/20"
    }
  ];

  const navigationLinks = [
    { href: "/", label: "Home", icon: "🏠" },
    { href: "#services", label: "Services", icon: "⚡" },
    { href: "#about", label: "Über mich", icon: "👨‍💻" },
    { href: "#projects", label: "Projekte", icon: "🚀" },
    { href: "/termin-buchen", label: "Termin buchen", icon: "📅" }
  ];

  const achievements = [
    { number: "10+", label: "Premium Projekte", icon: <Star className="w-4 h-4" /> },
    { number: "100%", label: "Kundenzufriedenheit", icon: <Heart className="w-4 h-4" /> },
    { number: "5+", label: "Jahre Expertise", icon: <Code className="w-4 h-4" /> },
    { number: "24h", label: "Response Time", icon: <Zap className="w-4 h-4" /> }
  ];

  return (
    <footer className="relative bg-gradient-to-b from-gray-900 via-black to-gray-900 text-white overflow-hidden">
      {/* Background Animation */}
      <div className="absolute inset-0 z-0">
        <motion.div
          animate={{
            rotate: [0, 360],
            scale: [1, 1.1, 1],
          }}
          transition={{
            duration: 20,
            repeat: Infinity,
            ease: "linear"
          }}
          className="absolute -top-32 -right-32 w-96 h-96 bg-gradient-to-br from-blue-600/20 to-purple-600/20 rounded-full blur-3xl"
        />
        <motion.div
          animate={{
            rotate: [360, 0],
            scale: [1, 1.2, 1],
          }}
          transition={{
            duration: 25,
            repeat: Infinity,
            ease: "linear"
          }}
          className="absolute -bottom-32 -left-32 w-80 h-80 bg-gradient-to-br from-purple-600/20 to-pink-600/20 rounded-full blur-3xl"
        />
        
        {/* Animated Grid Pattern */}
        <div className="absolute inset-0 opacity-10">
          <div className="absolute inset-0 bg-gradient-to-br from-white/5 to-transparent"></div>
        </div>
      </div>

      <motion.div
        ref={ref}
        variants={containerVariants}
        initial="hidden"
        animate={inView ? "visible" : "hidden"}
        className="relative z-10 pt-20 pb-8"
      >
        <div className="container mx-auto px-4">
          {/* Header Section */}
          <motion.div variants={itemVariants} className="text-center mb-16">
            <motion.div
              initial={{ scale: 0 }}
              animate={inView ? { scale: 1 } : {}}
              transition={{ duration: 0.6, type: "spring" }}
              className="inline-flex items-center justify-center px-6 py-2 mb-6 text-sm font-semibold text-blue-300 bg-blue-900/30 backdrop-blur-sm rounded-full border border-blue-500/30"
            >
              <Sparkles className="w-4 h-4 mr-2" />
              SWISS DIGITAL EXCELLENCE
            </motion.div>
            
            <h2 className="text-4xl md:text-5xl font-bold mb-6">
              <span className="bg-clip-text text-transparent bg-gradient-to-r from-blue-400 via-purple-400 to-pink-400">
                Bereit für deine
              </span>
              <span className="block text-white">digitale Transformation?</span>
            </h2>
            
            <p className="text-xl text-gray-300 max-w-2xl mx-auto mb-8">
              Lass uns gemeinsam deine Vision in die Realität umsetzen.
            </p>

            <Link to="/termin-buchen">
              <motion.button
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.95 }}
                className="group relative inline-flex items-center px-8 py-4 text-lg font-semibold text-white bg-gradient-to-r from-blue-600 to-purple-600 rounded-full overflow-hidden transition-all duration-300 hover:shadow-2xl"
              >
                <span className="relative z-10">Kostenloses Gespräch sichern</span>
                <ArrowRight className="ml-2 w-5 h-5 relative z-10 group-hover:translate-x-1 transition-transform" />
                <motion.div
                  className="absolute inset-0 bg-gradient-to-r from-purple-600 to-pink-600"
                  initial={{ x: "-100%" }}
                  whileHover={{ x: 0 }}
                  transition={{ duration: 0.3 }}
                />
              </motion.button>
            </Link>
          </motion.div>

          {/* Achievements Grid */}
          <motion.div variants={itemVariants} className="grid grid-cols-2 md:grid-cols-4 gap-6 mb-16">
            {achievements.map((achievement, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, scale: 0.8 }}
                animate={inView ? { opacity: 1, scale: 1 } : {}}
                transition={{ duration: 0.5, delay: index * 0.1 }}
                whileHover={{ y: -5, scale: 1.05 }}
                className="text-center bg-white/5 backdrop-blur-sm rounded-2xl p-6 border border-white/10 hover:border-white/20 transition-all duration-300"
              >
                <div className="flex justify-center items-center mb-3">
                  <div className="text-blue-400">{achievement.icon}</div>
                </div>
                <div className="text-3xl font-bold bg-clip-text text-transparent bg-gradient-to-r from-blue-400 to-purple-400 mb-1">
                  {achievement.number}
                </div>
                <div className="text-sm text-gray-300">{achievement.label}</div>
              </motion.div>
            ))}
          </motion.div>

          {/* Main Footer Content */}
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8 mb-12">
            {/* Company Info */}
            <motion.div variants={itemVariants} className="lg:col-span-2">
              <motion.div
                initial={{ opacity: 0, x: -20 }}
                animate={inView ? { opacity: 1, x: 0 } : {}}
                transition={{ duration: 0.6 }}
                className="mb-6"
              >
                <span className="text-3xl font-bold bg-clip-text text-transparent bg-gradient-to-r from-blue-400 via-purple-400 to-pink-400">
                  OetterliApps
                </span>
                <div className="mt-2 text-sm text-gray-400">
                  Swiss Digital Excellence since 2019
                </div>
              </motion.div>
              
              <p className="text-gray-300 mb-6 leading-relaxed">
                Ich erschaffe <span className="font-semibold text-white">ultra-qualitative Apps</span>, 
                <span className="font-semibold text-white"> Webseiten</span> und 
                <span className="font-semibold text-white"> KI-Integrationen</span>, die dein Business transformieren. 
                Swiss Made. Premium Quality. Future Ready.
              </p>
              
              <div className="flex space-x-3">
                {socialLinks.map((social, index) => (
                  <motion.a
                    key={social.label}
                    href={social.href}
                    target="_blank"
                    rel="noopener noreferrer"
                    variants={socialVariants}
                    whileHover={{ scale: 1.1, rotate: 5 }}
                    whileTap={{ scale: 0.9 }}
                    className={`p-3 rounded-full bg-white/5 backdrop-blur-sm border border-white/10 text-gray-300 ${social.color} ${social.bgColor} transition-all duration-300 hover:border-white/20`}
                  >
                    <social.icon className="w-5 h-5" />
                  </motion.a>
                ))}
              </div>
            </motion.div>
            
            {/* Navigation */}
            <motion.div variants={itemVariants}>
              <h3 className="text-xl font-bold text-white mb-6 flex items-center">
                <div className="w-2 h-6 bg-gradient-to-b from-blue-400 to-purple-400 rounded-full mr-3"></div>
                Navigation
              </h3>
              <ul className="space-y-3">
                {navigationLinks.map((link, index) => (
                  <motion.li
                    key={link.label}
                    initial={{ opacity: 0, x: -10 }}
                    animate={inView ? { opacity: 1, x: 0 } : {}}
                    transition={{ duration: 0.5, delay: index * 0.05 }}
                  >
                    <motion.div
                      whileHover={{ x: 5 }}
                      className="group"
                    >
                      {link.href.startsWith('#') ? (
                        <a 
                          href={link.href} 
                          className="flex items-center text-gray-300 hover:text-white transition-colors duration-300"
                        >
                          <span className="mr-3 text-lg">{link.icon}</span>
                          {link.label}
                          <ArrowRight className="w-4 h-4 ml-auto opacity-0 group-hover:opacity-100 transition-opacity" />
                        </a>
                      ) : (
                        <Link 
                          to={link.href} 
                          className="flex items-center text-gray-300 hover:text-white transition-colors duration-300"
                        >
                          <span className="mr-3 text-lg">{link.icon}</span>
                          {link.label}
                          <ArrowRight className="w-4 h-4 ml-auto opacity-0 group-hover:opacity-100 transition-opacity" />
                        </Link>
                      )}
                    </motion.div>
                  </motion.li>
                ))}
              </ul>
            </motion.div>
            
            {/* Contact Info */}
            <motion.div variants={itemVariants}>
              <h3 className="text-xl font-bold text-white mb-6 flex items-center">
                <div className="w-2 h-6 bg-gradient-to-b from-purple-400 to-pink-400 rounded-full mr-3"></div>
                Kontakt
              </h3>
              <div className="space-y-4">
                <motion.div
                  whileHover={{ x: 5 }}
                  className="flex items-start text-gray-300"
                >
                  <MapPin className="w-5 h-5 mr-3 mt-0.5 text-blue-400" />
                  <div>
                    <div className="font-medium text-white">OetterliApps</div>
                    <div>Sappetenstrasse 14</div>
                    <div>4416 Bubendorf, CH</div>
                  </div>
                </motion.div>
                
                <motion.div
                  whileHover={{ x: 5 }}
                  className="flex items-center"
                >
                  <Mail className="w-5 h-5 mr-3 text-green-400" />
                  <a 
                    href="mailto:patrick.oetterli@gmail.com" 
                    className="text-gray-300 hover:text-white transition-colors duration-300"
                  >
                    patrick.oetterli@gmail.com
                  </a>
                </motion.div>
                
                <motion.div
                  whileHover={{ x: 5 }}
                  className="flex items-center"
                >
                  <Phone className="w-5 h-5 mr-3 text-purple-400" />
                  <a 
                    href="tel:+41791234567" 
                    className="text-gray-300 hover:text-white transition-colors duration-300"
                  >
                    +41 79 123 45 67
                  </a>
                </motion.div>
              </div>
            </motion.div>
          </div>
          
          {/* Bottom Section */}
          <motion.div 
            variants={itemVariants}
            className="border-t border-white/10 pt-8"
          >
            <div className="flex flex-col md:flex-row justify-between items-center">
              <motion.p
                initial={{ opacity: 0 }}
                animate={inView ? { opacity: 1 } : {}}
                transition={{ duration: 0.6, delay: 0.5 }}
                className="text-gray-400 text-center md:text-left mb-4 md:mb-0"
              >
                &copy; {new Date().getFullYear()} OetterliApps. Made with{' '}
                <motion.span
                  animate={{ scale: [1, 1.2, 1] }}
                  transition={{ duration: 1, repeat: Infinity }}
                  className="inline-block text-red-400"
                >
                  ❤️
                </motion.span>
                {' '}in Switzerland.
              </motion.p>
              
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={inView ? { opacity: 1, y: 0 } : {}}
                transition={{ duration: 0.6, delay: 0.6 }}
                className="flex space-x-6"
              >
                <Link 
                  to="/impressum" 
                  className="text-gray-400 hover:text-white transition-colors duration-300 text-sm"
                >
                  Impressum
                </Link>
                <Link 
                  to="/datenschutz" 
                  className="text-gray-400 hover:text-white transition-colors duration-300 text-sm"
                >
                  Datenschutz
                </Link>
                <Link 
                  to="/agb" 
                  className="text-gray-400 hover:text-white transition-colors duration-300 text-sm"
                >
                  AGB
                </Link>
              </motion.div>
            </div>
          </motion.div>
        </div>
      </motion.div>
    </footer>
  );
};

export default Footer;